import { Ability } from "@/interfaces/ability";
import { CustomAxiosRequestConfig } from "@/interfaces/customAxiosRequestConfig";
import axios from "@/lib/axios";
import { useRoleAbilitiesStore } from "@/stores/roleAbilities";
import { useMutation, useQuery } from "@tanstack/vue-query";
import { storeToRefs } from "pinia";
import { watch } from "vue";
import { ToastInterface, useToast } from "vue-toastification";

interface Params {
  immediate?: boolean;
  roleID: number;
}

const getRoleAbility = async (roleID: number): Promise<Ability[]> => {
  const { data } = await axios.get<Ability[]>(`api/roles/${roleID}/abilities`);
  return data;
};

const postRoleAbility = async (
  roleID: number,
  abilities: number[],
  toast: ToastInterface,
) => {
  try {
    await axios.post(
      `api/roles/${roleID}/abilities`,
      { abilities },
      { errorHandling: false } as CustomAxiosRequestConfig,
    );
    toast.success("Permisos actualizados correctamente");
  } catch (error: any) {
    toast.error(error?.response?.data?.message ?? "No tienes permiso para otorgar estos permisos");
    // Re-lanzar para que la mutación RECHACE: el guardado tiene que señalar el
    // fallo (403 de plan/authz, 422, red) para que el guard NO cambie de rol ni
    // pise los toggles del usuario. Sin esto, un guardado fallido perdía los
    // cambios en silencio.
    throw error;
  }
};

const useRoleAbility = (props: Params) => {
  const { roleAbilities } = storeToRefs(useRoleAbilitiesStore());
  const { setRoleAbilities, setRoleId } = useRoleAbilitiesStore();
  const { immediate = true } = props;
  const toast = useToast();

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["roleAbility", props.roleID],
    queryFn: () => getRoleAbility(props.roleID),
    enabled: immediate,
  });

  const postRoleAbilityMutation = useMutation((abilities: number[]) =>
    postRoleAbility(props.roleID, abilities, toast),
  );

  watch(
    data,
    (value) => {
      if (value) {
        setRoleAbilities(
          value.map((ability) => ({
            ...ability,
            checked: ability.isActive,
          })),
        );
      }
    },
    { immediate, deep: true },
  );

  return {
    roleAbilities,
    isLoading,
    refetch,
    isFetching,
    setRoleId,

    postRoleAbilityMutation,
    postRoleAbilityMutationAsync: postRoleAbilityMutation.mutateAsync,
    postRoleAbilityMutationLoading: postRoleAbilityMutation.isLoading,
  };
};

export default useRoleAbility;
